import type { AgentRole, ToolInvocation } from "./agents";
import type { JsonObject, JsonValue } from "./types";
import { ToolExecutor, type ToolExecutionResult } from "./tool-runtime";

export interface AgentLoopMessage { role: "user" | "assistant"; content: string; toolCall?: { id: string; toolContractId: string; arguments: JsonObject; thoughtSignature?: string; providerContinuation?: JsonObject[]; batchId?: string }; toolResult?: { id: string; toolContractId: string }; }
export interface AgentLoopEvent { type: string; delta?: string; status?: string; toolContractId?: string | null; providerCallId?: string; arguments?: unknown; argumentsJson?: string; [key: string]: unknown; }
export interface AgentLoopTransportRequest {
  messages: AgentLoopMessage[];
  role: AgentRole;
  enabledToolIds: string[];
  phase: "planning" | "narration" | "postprocessing" | "resume";
  step: number;
}
export type AgentLoopTransport = (request: AgentLoopTransportRequest, onEvent: (event: AgentLoopEvent) => void) => Promise<{ terminal?: AgentLoopEvent | null }>;

export interface AgentLoopToolReceipt {
  invocation: ToolInvocation;
  result: ToolExecutionResult;
  approved: boolean;
}

export interface RunAgentLoopInput {
  runId: string;
  storyId: string;
  role: AgentRole;
  phase: AgentLoopTransportRequest["phase"];
  enabledToolIds: string[];
  messages: AgentLoopMessage[];
  executor: ToolExecutor;
  transport: AgentLoopTransport;
  approve?: (invocation: ToolInvocation) => Promise<boolean> | boolean;
  onTextDelta?: (delta: string, step: number) => void;
  onToolReceipt?: (receipt: AgentLoopToolReceipt) => void;
  onToolStart?: (invocation: ToolInvocation) => void;
  maxSteps?: number;
  signal?: AbortSignal;
  now?: () => string;
  sourceMessageIds?: readonly string[];
  resume?: AgentLoopCheckpoint;
  onCheckpoint?: (checkpoint: AgentLoopCheckpoint) => Promise<void>;
  onRequestStart?: (checkpoint: AgentLoopCheckpoint) => Promise<void>;
}

/** Resume the same provider batch and budget, including native thought signatures. */
export interface AgentLoopCheckpoint {
  runId:string; step:number; messages:AgentLoopMessage[]; receipts:AgentLoopToolReceipt[];
  pendingTools:AgentLoopEvent[]; pendingIndex:number; finalText:string; repairAttempts:number;
  emptyResponseRetries?:number;
}

export interface AgentLoopResult {
  text: string;
  messages: AgentLoopMessage[];
  receipts: AgentLoopToolReceipt[];
  steps: number;
  terminal?: AgentLoopEvent | null;
}

const asJsonObject = (value: unknown): JsonObject | null => value && typeof value === "object" && !Array.isArray(value) ? value as JsonObject : null;
const safeResult = (result: ToolExecutionResult): JsonValue => ({ ok: result.ok, status: result.status, ...(result.result === undefined ? {} : { result: result.result }), ...(result.error ? { error: result.error } : {}), changedDocumentIds: result.changedDocumentIds });

/** A later successful retry must concern the same operation and target. Reads
 * or unrelated writes cannot erase a failed mutation from a staged turn. */
export function unresolvedToolReceipts(receipts: readonly AgentLoopToolReceipt[]): AgentLoopToolReceipt[] {
  const pending = new Map<string, AgentLoopToolReceipt>();
  for (const receipt of receipts) {
    const args = receipt.invocation.arguments;
    const identity = Object.fromEntries(Object.entries(args).filter(([key]) => ["fileId", "fileIds", "sourceId", "targetId", "entityId", "locationId", "command", "action", "slug", "fileTypeId"].includes(key)).sort(([a], [b]) => a.localeCompare(b)));
    if (Array.isArray(args.operations)) identity.operations = args.operations.map(operation => operation && typeof operation === "object" && !Array.isArray(operation) ? { op: operation.op ?? null, path: operation.path ?? null } : operation);
    const key = `${receipt.invocation.toolContractId}:${JSON.stringify(identity)}`;
    if (receipt.result.ok) pending.delete(key);
    else pending.set(key, receipt);
  }
  return [...pending.values()];
}

/** Provider-independent tool loop. Tool results are compact, explicit turns. */
export async function runAgentLoop(input: RunAgentLoopInput): Promise<AgentLoopResult> {
  const maxSteps = Math.max(1, Math.min(48, Math.trunc(input.maxSteps ?? 8)));
  const now = input.now ?? (() => new Date().toISOString());
  if(input.resume && (input.resume.runId!==input.runId || !Number.isInteger(input.resume.step) || input.resume.step<0 || input.resume.step>=maxSteps)) throw new Error('Invalid or exhausted resumed agent budget');
  if(input.resume?.emptyResponseRetries !== undefined && (!Number.isInteger(input.resume.emptyResponseRetries) || input.resume.emptyResponseRetries < 0 || input.resume.emptyResponseRetries > 1)) throw new Error('Invalid resumed empty response allowance');
  if (input.resume && (!Array.isArray(input.resume.pendingTools) || !Number.isInteger(input.resume.pendingIndex) || input.resume.pendingIndex < 0 || input.resume.pendingIndex > input.resume.pendingTools.length || !Number.isInteger(input.resume.repairAttempts) || input.resume.repairAttempts < 0)) throw new Error('Invalid resumed provider batch');
  const messages = structuredClone(input.resume?.messages ?? input.messages);
  const receipts: AgentLoopToolReceipt[] = structuredClone(input.resume?.receipts ?? []);
  let finalText = input.resume?.finalText ?? "";
  let repairAttempts = input.resume?.repairAttempts ?? 0;
  let emptyResponseRetries = input.resume?.emptyResponseRetries ?? 0;
  let terminal: AgentLoopEvent | null | undefined;
  for (let step = input.resume?.step ?? 0; step < maxSteps; step += 1) {
    if (input.signal?.aborted) throw new DOMException("Agent stopped", "AbortError");
    const resumingBatch = input.resume && step===input.resume.step;
    const toolEvents: AgentLoopEvent[] = resumingBatch ? structuredClone(input.resume!.pendingTools) : [];
    let hasFailedTool = false;
    let stepText = "";
    if(!resumingBatch) await input.onRequestStart?.(structuredClone({runId:input.runId,step,messages,receipts,pendingTools:[],pendingIndex:0,finalText,repairAttempts,emptyResponseRetries}));
    const response = resumingBatch ? {} : await input.transport({ messages: structuredClone(messages), role: input.role, enabledToolIds: [...input.enabledToolIds], phase: step === 0 ? input.phase : "resume", step }, (event) => {
      if (event.type === "delta" && typeof event.delta === "string") { stepText += event.delta; input.onTextDelta?.(event.delta, step); }
      if (event.type === "tool_call") toolEvents.push(event);
    });
    terminal = response.terminal;
    // A completed empty decision has no tools to replay. Permit one fresh
    // request within the same budget; checkpoint it so reload cannot reset it.
    if (terminal?.status === 'empty_response' && input.phase === 'planning' && !toolEvents.length && !stepText) {
      const retry = emptyResponseRetries < 1 && step + 1 < maxSteps;
      if (retry) emptyResponseRetries++;
      await input.onCheckpoint?.(structuredClone({runId:input.runId,step,messages,receipts,pendingTools:[],pendingIndex:0,finalText,repairAttempts,emptyResponseRetries}));
      if (retry) continue;
    }
    if (terminal?.status && !["completed", "tool_calls"].includes(terminal.status) && !(input.phase === "narration" && ["max_tokens", "upstream_disconnected"].includes(terminal.status))) {
      const reasons: Record<string, string> = { empty_response: "โมเดลส่งคำตอบว่างและไม่ได้เรียกเครื่องมือ", upstream_disconnected: "การเชื่อมต่อโมเดลสิ้นสุดก่อนตอบครบ", max_tokens: "โมเดลใช้โควตาคำตอบหมดก่อนทำงานเสร็จ", provider_error: "ผู้ให้บริการโมเดลปฏิเสธคำขอเครื่องมือ โปรดลองใหม่หรือเลือกโมเดลอื่น", provider_refusal: "โมเดลปฏิเสธคำขอนี้" };
      throw new Error(reasons[terminal.status] || `โมเดลทำงานไม่สำเร็จ (${terminal.status})`);
    }
    if (!toolEvents.length && !resumingBatch) {
      if (input.role === "creator_copilot" && receipts.length === 0 && step === 0 && repairAttempts < 2) {
        repairAttempts++;
        messages.push({
          role: "user",
          content: "คำสั่งระบบ: คุณเป็น Movis Agent ใน Studio มีหน้าที่ลงมือทำงานและตรวจสอบไฟล์จริง ห้ามคุยปกติ ห้ามตอบลอยๆ หรืออ้างว่าทำเสร็จแล้วโดยไม่เรียกเครื่องมือเด็ดขาด! คุณต้องเรียกเครื่องมือ (เช่น world.search, world.read, world.list_file_types, world.create_file, world.patch) เพื่อตรวจสอบ อ่าน หรือจัดการไฟล์จริงในโปรเจกต์ก่อน แล้วจึงสรุปผลจากการเรียกเครื่องมือเท่านั้น จงเรียกเครื่องมือเดี๋ยวนี้",
        });
        continue;
      }
      return { text: input.role === "creator_copilot" ? stepText : finalText + stepText, messages, receipts, steps: step + 1, ...(terminal === undefined ? {} : { terminal }) };
    }
    finalText += stepText;
    const checkpoint = (pendingIndex:number) => input.onCheckpoint?.(structuredClone({runId:input.runId,step,messages,receipts,pendingTools:toolEvents,pendingIndex,finalText,repairAttempts,emptyResponseRetries}));
    for (let index = resumingBatch ? input.resume!.pendingIndex : 0; index < toolEvents.length; index += 1) {
      if (input.signal?.aborted) throw new DOMException("Agent stopped", "AbortError");
      const event = toolEvents[index]!;
      const toolContractId = typeof event.toolContractId === "string" ? event.toolContractId : "";
      let parsed: unknown = event.arguments;
      if (parsed == null && typeof event.argumentsJson === "string") try { parsed = JSON.parse(event.argumentsJson); } catch { parsed = null; }
      const rawArguments = asJsonObject(parsed);
      const providerCallId = String(event.providerCallId || `${step}:${index}`);
      if (!toolContractId || !input.enabledToolIds.includes(toolContractId) || !rawArguments) {
        const invalid: ToolExecutionResult = { ok: false, status: "denied", error: !rawArguments ? "Tool arguments are not valid JSON" : "Tool is outside the active Agent scope", changedDocumentIds: [] };
        messages.push({ role: "assistant", content: `[AGENT_TOOL_CALL] ${JSON.stringify({ providerCallId, toolContractId })}` });
        messages.push({ role: "user", content: `[AGENT_TOOL_RESULT] ${JSON.stringify(safeResult(invalid))}` });
        continue;
      }
      const expectedRevisionValue = rawArguments.expectedRevision;
      const expectedRevision = Number.isInteger(Number(expectedRevisionValue)) ? Number(expectedRevisionValue) : undefined;
      const contract = input.executor.registry.get(toolContractId)?.contract;
      const arguments_: JsonObject = Object.fromEntries(Object.entries(rawArguments).filter(([key]) => key !== "expectedRevision")) as JsonObject;
      if (contract?.mutatesState && !Array.isArray(arguments_.sourceMessageIds) && input.sourceMessageIds?.length) {
        arguments_.sourceMessageIds = [...new Set(input.sourceMessageIds.filter(Boolean))].slice(0, 200);
      }
      const invocation: ToolInvocation = {
        id: providerCallId, runId: input.runId, toolContractId, agentRole: input.role, arguments: arguments_,
        ...(expectedRevision == null ? {} : { expectedRevision }), idempotencyKey: `${input.runId}:${providerCallId}`, createdAt: now(),
      };
      const approvalNeeded = contract?.approval === "always" || (contract?.approval === "write" && contract.mutatesState);
      const approved = !approvalNeeded || Boolean(await input.approve?.(invocation));
      await checkpoint(index);
      input.onToolStart?.(invocation);
      const result = await input.executor.execute(invocation, { storyId: input.storyId, runId: input.runId, agentRole: input.role, approved, now: now() });
      // A suspended player roll is still pending when the page closes. Never
      // acknowledge cancellation as a tool result and skip that command on resume.
      if(input.signal?.aborted) throw new DOMException('Agent stopped','AbortError');
      const receipt = { invocation, result, approved };
      receipts.push(receipt); input.onToolReceipt?.(receipt);
      messages.push({ role: "assistant", content: "", toolCall: { id: providerCallId, toolContractId, arguments: rawArguments, batchId: `${input.runId}:${step}`, ...(Array.isArray(event.providerContinuation) ? { providerContinuation: event.providerContinuation as JsonObject[] } : {}), ...(typeof event.thoughtSignature === "string" ? { thoughtSignature: event.thoughtSignature } : {}) } });
      messages.push({ role: "user", content: JSON.stringify(safeResult(result)), toolResult: { id: providerCallId, toolContractId } });
      if (!result.ok && result.status === "failed") {
        if (/cancelled|canceled|AbortError|ยกเลิก/i.test(result.error || "")) {
          await checkpoint(toolEvents.length);
          return { text: input.role === "creator_copilot" ? "" : finalText, messages, receipts, steps: step + 1, ...(terminal === undefined ? {} : { terminal }) };
        }
        hasFailedTool = true;
      }
      await checkpoint(index+1);
    }
    if (hasFailedTool) {
      const maxRepairAttempts = Math.max(3, Math.min(6, maxSteps - step - 1));
      if (["creator_copilot", "main_gm"].includes(input.role) && repairAttempts++ < maxRepairAttempts) {
        await checkpoint(toolEvents.length);
        continue;
      }
      await checkpoint(toolEvents.length);
      return { text: input.role === "creator_copilot" ? "" : finalText, messages, receipts, steps: step + 1, ...(terminal === undefined ? {} : { terminal }) };
    }
  }
  throw new Error(`Agent tool loop exceeded ${maxSteps} steps`);
}
